import { getTrustedDeviceCookie } from 'utils/cookies/getCookies';
import types from './types';

const {
  AUTH_CALLBACK, AUTH_INIT,
  LOGIN, LOGIN_SUCCESS, LOGIN_FAIL,
  REFRESH, REFRESH_SUCCESS, REFRESH_FAIL,
  VERIFY, VERIFY_SUCCESS, VERIFY_FAIL,
  RESEND, RESEND_SUCCESS, RESEND_FAIL, RESEND_CLEAR,
  LOAD_OWNERS, LOAD_OWNERS_SUCCESS, LOAD_OWNERS_FAIL,
  SEND_REQUEST, SEND_REQUEST_SUCCESS, SEND_REQUEST_FAIL,
  VALIDATE_MOBILE, VALIDATE_MOBILE_SUCCESS, VALIDATE_MOBILE_FAIL,
  SELECT_2FA_DELIVERY_METHOD,
  SET_USER, CLEAN_UP,
  PASSKEY_LOGIN, PASSKEY_LOGIN_SUCCESS, PASSKEY_LOGIN_FAIL, PASSKEY_LOGIN_OPTIONS_SUCCESS,
  PASSKEY_REGISTER_OPTIONS, PASSKEY_REGISTER_OPTIONS_SUCCESS, PASSKEY_REGISTER_OPTIONS_FAIL,
  PASSKEY_REGISTER_VERIFY, PASSKEY_REGISTER_VERIFY_SUCCESS, PASSKEY_REGISTER_VERIFY_FAIL,
  PASSKEY_LIST, PASSKEY_LIST_SUCCESS, PASSKEY_LIST_FAIL,
  PASSKEY_LIST_FOR_OWNER, PASSKEY_LIST_FOR_OWNER_SUCCESS, PASSKEY_LIST_FOR_OWNER_FAIL,
  PASSKEY_RENAME, PASSKEY_RENAME_SUCCESS, PASSKEY_RENAME_FAIL,
  PASSKEY_REVOKE, PASSKEY_REVOKE_SUCCESS, PASSKEY_REVOKE_FAIL,
  PASSKEY_REGISTERED,
  SESSION_VERIFIED,
  RECOVERY_STATUS, RECOVERY_STATUS_SUCCESS, RECOVERY_STATUS_FAIL,
  RECOVERY_STATUS_FOR_OWNER, RECOVERY_STATUS_FOR_OWNER_SUCCESS, RECOVERY_STATUS_FOR_OWNER_FAIL,
  RECOVERY_GENERATE, RECOVERY_GENERATE_SUCCESS, RECOVERY_GENERATE_FAIL,
} = types;

export function cleanup() {
  return { type: CLEAN_UP };
}

export function clearResend2FaStatus() {
  return { type: RESEND_CLEAR };
}

export function authenticate(provider, tenant, code) {
  // If code is provided, validate the authentication
  if (provider === 'google' && code) {
    const trustedDeviceId = getTrustedDeviceCookie();
    const params = trustedDeviceId ? { code, trustedDeviceId } : { code };

    return {
      types: [LOGIN, AUTH_CALLBACK, LOGIN_FAIL],
      promise: client => client.get(`/OwnerAuth/${provider}/callback`, { params, tenant }),
    };
  }

  // Otherwise initialize the authentication
  return {
    types: [LOGIN, AUTH_INIT, LOGIN_FAIL],
    promise: client => client.get(`/OwnerAuth/${provider}`, { tenant }),
  };
}

export function login(username, password, tenant) {
  const trustedDeviceId = getTrustedDeviceCookie();
  const data = { username, password, trustedDeviceId };

  return {
    types: [LOGIN, LOGIN_SUCCESS, LOGIN_FAIL],
    promise: client => client.post(`/OwnerAuth/login?tenant=${tenant}`, { data }),
  };
}

export function logout() {
  // Navigating to the logout path will allow the server-side to handle the logout request and then easily reset the application state
  window.location.assign(`${window.location.origin}/logout`);
}

export function refresh() {
  return {
    types: [REFRESH, REFRESH_SUCCESS, REFRESH_FAIL],
    promise: client => client.post('/OwnerAuth/refresh'),
  };
}

export function resend2faCode(username, tenant, recipientHash, deliveryMethod, twoFactorRequestId) {
  // In the case of logging when when a 2FA option is forced (such as admin login), the recipientHash and deliveryMethod will be null
  // since the user will not have selected a recipient or delivery method.
  // However a null value for recipientHash and deliveryMethod must not be included as "null" in the payload, so explicitly set it as undefined.
  const data = {
    username,
    tenant,
    recipientHash: recipientHash ?? undefined,
    deliveryMethod: deliveryMethod ?? undefined,
    twoFactorRequestId: twoFactorRequestId ?? undefined,
  };

  return {
    types: [RESEND, RESEND_SUCCESS, RESEND_FAIL],
    promise: client => client.post(`/OwnerAuth/resend2faCode?tenant=${tenant}`, { data }),
  };
}

export function getOwners(token) {
  const filter = { include: [{ relation: 'contracts', scope: { include: ['property'] } }] };
  return {
    types: [LOAD_OWNERS, LOAD_OWNERS_SUCCESS, LOAD_OWNERS_FAIL],
    promise: client => client.get(`/owners/find?filter=${encodeURIComponent(JSON.stringify(filter))}`, { token }),
  };
}

export function setUser(user) {
  return { type: SET_USER, user };
}

export function requestInfoUpdates(userId, data, tenant) {
  return {
    types: [SEND_REQUEST, SEND_REQUEST_SUCCESS, SEND_REQUEST_FAIL],
    notifications: [null, 'notifications.requestInfoUpdates.successful', null],
    promise: client => client.post(`/owners/${userId}/requestInfoUpdates`, { data, tenant }),
  };
}

export function verify2faCode(username, tenant, twoFactorRequestId, code) {
  const trustedDeviceId = getTrustedDeviceCookie();

  return {
    types: [VERIFY, VERIFY_SUCCESS, VERIFY_FAIL],
    promise: client => client.post('/OwnerAuth/verify2faCode', { data: { username, tenant, twoFactorRequestId, code, trustDevice: trustedDeviceId } }),
  };
}

// --- Passkey (WebAuthn) authentication ---------------------------------------
// The heavy lifting (WebAuthn ceremony via navigator.credentials) lives in the
// usePasskey() hook; these actions only wrap the gateway HTTP calls the hook's
// PasskeyClient invokes. A verified assertion starts a session, so
// PASSKEY_LOGIN_SUCCESS is handled identically to LOGIN_SUCCESS in middleware.

export function passkeyAuthenticateOptions(tenant) {
  return {
    types: [PASSKEY_LOGIN, PASSKEY_LOGIN_OPTIONS_SUCCESS, PASSKEY_LOGIN_FAIL],
    promise: client => client.post('/OwnerAuth/passkey/authenticate/options', { tenant }),
  };
}

export function passkeyAuthenticateVerify(credential, tenant) {
  const trustedDeviceId = getTrustedDeviceCookie();
  return {
    types: [PASSKEY_LOGIN, PASSKEY_LOGIN_SUCCESS, PASSKEY_LOGIN_FAIL],
    promise: client => client.post('/OwnerAuth/passkey/authenticate/verify', {
      data: { credential, trustDevice: trustedDeviceId },
      tenant,
    }),
  };
}

// --- Passkey self-service management (requires an active session) ------------

export function passkeyRegisterOptions(deviceName, tenant) {
  return {
    types: [PASSKEY_REGISTER_OPTIONS, PASSKEY_REGISTER_OPTIONS_SUCCESS, PASSKEY_REGISTER_OPTIONS_FAIL],
    promise: client => client.post('/owners/passkey/register/options', { data: { deviceName }, tenant }),
  };
}

export function passkeyRegisterVerify(credential, deviceName, tenant) {
  return {
    types: [PASSKEY_REGISTER_VERIFY, PASSKEY_REGISTER_VERIFY_SUCCESS, PASSKEY_REGISTER_VERIFY_FAIL],
    promise: client => client.post('/owners/passkey/register/verify', { data: { credential, deviceName }, tenant }),
  };
}

export function passkeyList() {
  return {
    types: [PASSKEY_LIST, PASSKEY_LIST_SUCCESS, PASSKEY_LIST_FAIL],
    promise: client => client.get('/owners/passkey'),
  };
}

// OWN-229: admin-only, read-only view of a DIFFERENT owner's passkeys, for
// htm.admin impersonating that owner on Security & Login - passkeyList()
// above is self-scoped server-side and can't be reused for this.
export function passkeyListForOwner(ownerId) {
  return {
    types: [PASSKEY_LIST_FOR_OWNER, PASSKEY_LIST_FOR_OWNER_SUCCESS, PASSKEY_LIST_FOR_OWNER_FAIL],
    promise: client => client.get(`/owners/${ownerId}/passkey`),
  };
}

export function passkeyRename(id, deviceName) {
  return {
    types: [PASSKEY_RENAME, PASSKEY_RENAME_SUCCESS, PASSKEY_RENAME_FAIL],
    promise: client => client.patch(`/owners/passkey/${id}`, { data: { deviceName } }),
  };
}

export function passkeyRevoke(id, tenant) {
  return {
    types: [PASSKEY_REVOKE, PASSKEY_REVOKE_SUCCESS, PASSKEY_REVOKE_FAIL],
    promise: client => client.delete(`/owners/passkey/${id}`, { tenant }),
  };
}

// Local-only: flips hasPasskey/passkeyRegistrationRequired immediately after a
// successful registration, without waiting for the next login/refresh cycle
// (see FTR-3 mandatory-registration hard-block).
export function markPasskeyRegistered() {
  return { type: PASSKEY_REGISTERED };
}

// Local-only: flips sessionVerified once a step-up verification (passkey,
// password+2FA, or recovery code) succeeds on the VerificationRequired page -
// see grantAndGo there. Gates actions on the Security & Login page even if a
// protected route is somehow reached without going through /verify.
export function markSessionVerified() {
  return { type: SESSION_VERIFIED };
}

// --- Recovery access code ----------------------------------------------------

// How many codes from the current batch are still unused - a fresh batch
// can't be generated until this reaches 0.
export function recoveryStatus() {
  return {
    types: [RECOVERY_STATUS, RECOVERY_STATUS_SUCCESS, RECOVERY_STATUS_FAIL],
    promise: client => client.get('/owners/recovery/status'),
  };
}

// OWN-229: admin-only, read-only view of a DIFFERENT owner's recovery-code
// status, for htm.admin impersonating that owner on Security & Login -
// recoveryStatus() above is self-scoped server-side and can't be reused for
// this. No generate counterpart - display-only, same as passkeyListForOwner.
export function recoveryStatusForOwner(ownerId) {
  return {
    types: [RECOVERY_STATUS_FOR_OWNER, RECOVERY_STATUS_FOR_OWNER_SUCCESS, RECOVERY_STATUS_FOR_OWNER_FAIL],
    promise: client => client.get(`/owners/${ownerId}/recovery/status`),
  };
}

export function recoveryGenerate(tenant) {
  return {
    types: [RECOVERY_GENERATE, RECOVERY_GENERATE_SUCCESS, RECOVERY_GENERATE_FAIL],
    promise: client => client.post('/owners/recovery/generate', { tenant }),
  };
}

export function selected2FADeliveryMethod(deliveryMethod) {
  return {
    type: SELECT_2FA_DELIVERY_METHOD,
    deliveryMethod,
  };
}

export function clearSelected2FADeliveryMethod() {
  return {
    type: SELECT_2FA_DELIVERY_METHOD,
    deliveryMethod: null,
  };
}

// Currently, validate phone is used just for validating the user's profile.
// If it is used elsewhere or have another same method of validation. It's better to move to a dedicated redux module.
export function validateMobile(phoneNumber) {
  return {
    types: [VALIDATE_MOBILE, VALIDATE_MOBILE_SUCCESS, VALIDATE_MOBILE_FAIL],
    promise: client => client.post('/phones/validatePhoneNumber', { data: { phoneNumber } }),
  };
}
